import React, { useState, useRef, useMemo } from 'react';
import toast from 'react-hot-toast';
import { Edit } from 'lucide-react';

import Modal from '../base/Modal';
import { API_URL } from '../../services/api';
import { DataState } from '../../context/DataProvider';
import AutoGrowTextarea from '../AutoGrowTextarea';
import SchemaBuilderModal from '../NewWorkflow/modals/SchemaBuilderModal';
import {
  fieldsToSchema,
  schemaToFields,
} from '../NewWorkflow/utils/schemaUtils';

const ActionNodeModal = ({
  mode,
  nodeData,
  onClose,
  onSave,
  onDelete,
  appData,
  viewOnly,
}) => {
  const [app, setApp] = useState(nodeData?.data?.app || '');
  const [functionsList, setFunctionsList] = useState(
    nodeData?.data?.functions || [],
  );
  const [actionInstructions, setActionInstructions] = useState(
    nodeData?.data?.actionInstructions || '',
  );
  const [outputExpectation, setOutputExpectation] = useState(
    nodeData?.data?.outputExpectation || '',
  );
  const [addingFunction, setAddingFunction] = useState(false);
  const [loadingAI, setLoadingAI] = useState(false);
  const [showWarningModal, setShowWarningModal] = useState(false);
  const { userGoogleId } = DataState();

  // Add these new states
  const [actionNeedsApproval, setActionNeedsApproval] = useState(
    nodeData?.data?.actionNeedsApproval || false,
  );
  const [functionsForApproval, setFunctionsForApproval] = useState(
    nodeData?.data?.functionsForApproval || [],
  );
  const [showApprovalModal, setShowApprovalModal] = useState(false);

  // --- NEW STATE FOR JSON OUTPUT ---
  const [jsonOutput, setJsonOutput] = useState(
    nodeData?.data?.jsonOutput || false,
  );
  const [jsonOutputSchema, setJsonOutputSchema] = useState(
    nodeData?.data?.jsonOutputSchema || {
      title: 'Output',
      type: 'object',
      properties: {},
    },
  );
  const [isBuilderOpen, setIsBuilderOpen] = useState(false);

  // Store the initial action instructions for comparison on save.
  const initialActionInstructions = useRef(
    nodeData?.data?.actionInstructions || '',
  );

  // Find the currently selected app and its functions list.
  const selectedApp = appData.find((a) => a.value === app);
  const validFunctions = selectedApp?.functions || [];

  // Compute remaining functions that can be added manually.
  const remainingFunctions = validFunctions.filter(
    (f) => !functionsList.includes(f.label),
  );

  // Memoize the initial fields for the schema builder
  const initialSchemaFields = useMemo(
    () => schemaToFields(jsonOutputSchema),
    [jsonOutputSchema],
  );

  // --- NEW HANDLER for saving from SchemaBuilderModal ---
  const handleSchemaSave = ({ fields, modelName }) => {
    const newSchema = fieldsToSchema(fields, modelName);
    setJsonOutputSchema(newSchema);
    setIsBuilderOpen(false); // Close the builder modal
    toast.success('Output schema saved!');
  };

  // Helper to call the AI endpoint.
  const callDetermineFunctionsAPI = async () => {
    const inputNodes = (nodeData?.inputNodes || []).map((node) => {
      return {
        type: node.type,
        data: node.data,
      };
    });
    setLoadingAI(true);
    try {
      const response = await fetch(
        `${API_URL}/canvas/actionNode/determineFunctions`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${userGoogleId}`, // Dummy token added here
          },
          body: JSON.stringify({
            allFunctions: validFunctions.map((f) => f.label),
            previousFunctions: functionsList,
            // functions: validFunctions,
            inputNodes,
            app,
            actionInstructions,
          }),
          credentials: 'include',
        },
      );
      if (!response.ok) {
        throw new Error('API request failed');
      }
      const data = await response.json();
      const newFunctions = data.functions || [];
      setFunctionsList(newFunctions);

      // Filter out any approved functions that are no longer in the main functions list
      setFunctionsForApproval((prev) =>
        prev.filter((f) => newFunctions.includes(f)),
      );

      initialActionInstructions.current = actionInstructions;
      toast.success('Functions updated successfully');
      return newFunctions;
    } catch (error) {
      toast.error('Failed to determine functions. Please try again.');
      return null;
    } finally {
      setLoadingAI(false);
    }
  };

  // Final save function that actually saves the node.
  // Accept an optional functionsOverride parameter.
  const performSave = (functionsOverride) => {
    // If approval is disabled, ensure the approval list is empty for clean data.
    const finalFunctionsForApproval = actionNeedsApproval
      ? functionsForApproval
      : [];

    const updatedNode = {
      ...nodeData,
      data: {
        app,
        label: selectedApp?.label,
        functions:
          functionsOverride !== undefined ? functionsOverride : functionsList,
        actionInstructions,
        outputExpectation,
        actionNeedsApproval, // Add this line
        functionsForApproval: finalFunctionsForApproval, // And this line
        // --- ADD NEW FIELDS TO SAVED DATA ---
        jsonOutput,
        jsonOutputSchema: jsonOutput ? jsonOutputSchema : {}, // Save schema only if enabled
      },
    };
    onSave(updatedNode);
  };

  // Handle save button click.
  const handleSave = async () => {
    // Check if action instructions have changed (after trimming)
    if (
      actionInstructions.trim() &&
      actionInstructions.trim() !== initialActionInstructions.current.trim()
    ) {
      // Show warning modal to choose between using AI or ignoring.
      setShowWarningModal(true);
    } else {
      performSave();
    }
  };

  // Handle removal of a function from the functions list.
  const handleRemoveFunction = (index) => {
    const functionToRemove = functionsList[index];
    const updatedFunctions = [...functionsList];
    updatedFunctions.splice(index, 1);
    setFunctionsList(updatedFunctions);
    // Also remove the function from the approval list if it exists there
    setFunctionsForApproval((prev) =>
      prev.filter((f) => f !== functionToRemove),
    );
  };

  // Render warning modal.
  const renderWarningModal = () => (
    <Modal
      title="Warning"
      isOpen={true}
      onClose={() => setShowWarningModal(false)}
    >
      <div className="rounded-lg bg-zinc-900 p-4">
        <p className="mb-4 text-zinc-100">
          The action instructions have changed. Would you like to use AI to
          determine the functions required for the action execution?
        </p>
        <div className="flex justify-end gap-3">
          <button
            className="rounded-md border border-zinc-700 bg-zinc-800 px-4 py-2 text-zinc-300 transition duration-200 hover:bg-zinc-700 focus:outline-none focus:ring-2 focus:ring-zinc-500"
            onClick={() => {
              // Ignore: Just close warning modal.
              setShowWarningModal(false);
              performSave();
            }}
          >
            Ignore
          </button>
          <button
            className="rounded-md border border-blue-600 bg-[#5652CF] px-4 py-2 text-zinc-300 transition duration-200 hover:bg-blue-600 focus:outline-none focus:ring-2 focus:ring-[#5652CF]"
            onClick={async () => {
              // Use AI: Call API, update functions list, and update the stored instructions.
              const newFuncs = await callDetermineFunctionsAPI();
              if (newFuncs) {
                // Update the stored "initial" instructions so that further saves won't trigger the warning.
                // initialActionInstructions.current = actionInstructions;
                toast.success('Functions updated based on action instructions');
              }
              // Close warning modal but keep the modal open for further review.
              setShowWarningModal(false);
            }}
            disabled={loadingAI}
          >
            {loadingAI ? 'Loading...' : 'Determine using AI'}
          </button>
        </div>
      </div>
    </Modal>
  );

  // Render modal for managing functions that need approval.
  const renderApprovalModal = () => (
    <Modal
      title="Manage Functions for Approval"
      isOpen={showApprovalModal}
      onClose={() => setShowApprovalModal(false)}
    >
      <div className="rounded-lg bg-zinc-900 p-4">
        <p className="mb-4 text-zinc-300">
          Select the functions that require approval before execution.
        </p>
        <div className="mb-4 max-h-60 space-y-2 overflow-y-auto rounded-md border border-zinc-700 p-3">
          {functionsList.length > 0 ? (
            functionsList.map((func) => (
              <div key={func} className="flex items-center">
                <input
                  type="checkbox"
                  id={`func-approval-${func}`}
                  checked={functionsForApproval.includes(func)}
                  onChange={() => {
                    setFunctionsForApproval((prev) =>
                      prev.includes(func)
                        ? prev.filter((f) => f !== func)
                        : [...prev, func],
                    );
                  }}
                  className="h-4 w-4 rounded border-zinc-600 bg-zinc-800 text-[#5652CF] focus:ring-blue-600"
                />
                <label
                  htmlFor={`func-approval-${func}`}
                  className="ml-3 block text-sm font-medium text-zinc-100"
                >
                  {func}
                </label>
              </div>
            ))
          ) : (
            <p className="text-center text-zinc-400">
              No functions have been added to this action yet.
            </p>
          )}
        </div>
        <div className="flex justify-end">
          <button
            className="rounded-md bg-[#5652CF] px-4 py-2 text-white transition duration-200 hover:bg-blue-600"
            onClick={() => setShowApprovalModal(false)}
          >
            Done
          </button>
        </div>
      </div>
    </Modal>
  );

  return (
    <>
      <Modal
        title={
          viewOnly
            ? 'Action Node'
            : mode === 'add'
              ? 'Add Action Node'
              : 'Edit Action Node'
        }
        isOpen={true}
        onClose={onClose}
      >
        <div className="h-[80vh] overflow-y-auto rounded-lg bg-zinc-900 p-4 shadow-md">
          <div className="p-4">
            <label className="mb-2 block text-sm font-medium text-zinc-200">
              {viewOnly ? 'App' : 'Select App'}
            </label>
            <select
              disabled={viewOnly}
              className="mb-4 w-full rounded border border-zinc-700 bg-zinc-800 p-2 text-zinc-100 focus:outline-none focus:ring-2 focus:ring-[#5652CF]"
              value={app}
              onChange={(e) => {
                setApp(e.target.value);
                // Reset functions when app changes.
                setFunctionsList([]);
                setFunctionsForApproval([]); // Also reset the approval list
              }}
            >
              <option value="">Select App</option>
              {appData.map((app) => (
                <option key={app.value} value={app.value}>
                  {app.label}
                </option>
              ))}
            </select>

            {app && (
              <div className="mb-4">
                <div className="mb-2 flex items-center justify-between">
                  <label
                    htmlFor="function"
                    className="block text-sm font-medium text-zinc-200"
                  >
                    Functions
                  </label>
                  {!viewOnly && (
                    <button
                      className="rounded bg-[#5652CF] p-2 text-white"
                      onClick={callDetermineFunctionsAPI}
                      disabled={loadingAI || !actionInstructions.trim()}
                    >
                      {loadingAI ? 'Loading...' : 'AI'}
                    </button>
                  )}
                </div>
                <div className="flex flex-wrap gap-2">
                  {functionsList.map((func, idx) => (
                    <div
                      key={idx}
                      className="flex items-center rounded bg-zinc-800 px-3 py-1"
                    >
                      <span className="text-zinc-100">{func}</span>
                      {!viewOnly && (
                        <button
                          className="ml-2 text-red-500"
                          onClick={() => handleRemoveFunction(idx)}
                        >
                          ✕
                        </button>
                      )}
                    </div>
                  ))}
                  {!viewOnly && (
                    <div className="flex items-center">
                      {addingFunction ? (
                        // eslint-disable-next-line
                        <select
                          className="rounded border border-zinc-700 bg-zinc-800 p-2 text-zinc-100"
                          value=""
                          onChange={(e) => {
                            const newFunc = e.target.value;
                            if (newFunc) {
                              setFunctionsList([...functionsList, newFunc]);
                            }
                            setAddingFunction(false);
                          }}
                          onBlur={() => setAddingFunction(false)}
                        >
                          <option value="">Select function</option>
                          {remainingFunctions.map((f) => (
                            <option key={f.label} value={f.label}>
                              {f.label}
                            </option>
                          ))}
                        </select>
                      ) : (
                        <button
                          className="rounded bg-[#5652CF] p-2 text-white"
                          onClick={() => setAddingFunction(true)}
                        >
                          +
                        </button>
                      )}
                    </div>
                  )}
                </div>
              </div>
            )}

            <label
              htmlFor="action-intructions"
              className="mt-4 block text-sm font-medium text-zinc-200"
            >
              Action Instructions
            </label>

            <AutoGrowTextarea
              className="w-full resize-none rounded-md border border-zinc-700 bg-zinc-800 px-3 py-2 text-zinc-100 placeholder-zinc-500 transition duration-200 focus:border-transparent focus:outline-none focus:ring-2 focus:ring-[#5652CF]"
              placeholder="Enter instructions for this action"
              value={actionInstructions}
              disabled={viewOnly}
              onChange={(e) => setActionInstructions(e.target.value)}
            />

            <label
              htmlFor="output-expectation"
              className="mt-4 block text-sm font-medium text-zinc-200"
            >
              Output Expectation
            </label>
            <AutoGrowTextarea
              className="w-full resize-none rounded-md border border-zinc-700 bg-zinc-800 px-3 py-2 text-zinc-100 placeholder-zinc-500 transition duration-200 focus:border-transparent focus:outline-none focus:ring-2 focus:ring-[#5652CF]"
              placeholder="Enter output expectation"
              value={outputExpectation}
              disabled={viewOnly}
              onChange={(e) => setOutputExpectation(e.target.value)}
            />

            {/* --- NEW JSON OUTPUT SECTION --- */}
            {!viewOnly && (
              <div className="mt-4 border-t border-zinc-700 pt-4">
                <div className="flex items-center gap-3">
                  <input
                    type="checkbox"
                    id="jsonOutput"
                    checked={jsonOutput}
                    onChange={(e) => setJsonOutput(e.target.checked)}
                    className="h-4 w-4 rounded border-zinc-600 bg-zinc-800 text-[#5652CF] focus:ring-blue-600"
                  />
                  <label
                    htmlFor="jsonOutput"
                    className="text-sm font-medium text-zinc-200"
                  >
                    Enable Structured JSON Output
                  </label>
                </div>

                {jsonOutput && (
                  <div className="mt-3 rounded-lg bg-zinc-800/50 p-3">
                    <p className="mb-2 text-sm text-zinc-400">
                      {Object.keys(jsonOutputSchema?.properties || {}).length >
                      0
                        ? `Schema defined with ${Object.keys(jsonOutputSchema.properties).length} top-level fields.`
                        : 'No output schema defined yet.'}
                    </p>
                    <button
                      onClick={() => setIsBuilderOpen(true)}
                      className="flex items-center gap-2 rounded-md bg-blue-600 px-3 py-1.5 text-sm text-zinc-100 transition duration-200 hover:bg-blue-700"
                    >
                      <Edit size={14} />
                      {Object.keys(jsonOutputSchema?.properties || {}).length >
                      0
                        ? 'Edit Schema'
                        : 'Create Schema'}
                    </button>
                  </div>
                )}
              </div>
            )}

            {/* --- Approval Section --- */}
            {!viewOnly && (
              <>
                <div className="mt-4 flex items-center gap-3">
                  <input
                    type="checkbox"
                    id="actionNeedsApproval"
                    checked={actionNeedsApproval}
                    onChange={(e) => {
                      setActionNeedsApproval(e.target.checked);
                      // If turning off approval, clear the list of functions
                      if (!e.target.checked) {
                        setFunctionsForApproval([]);
                      }
                    }}
                    className="h-4 w-4 rounded border-zinc-600 bg-zinc-800 text-[#5652CF] focus:ring-blue-600"
                  />
                  <label
                    htmlFor="actionNeedsApproval"
                    className="text-sm font-medium text-zinc-200"
                  >
                    This action requires approval
                  </label>
                </div>

                {actionNeedsApproval && (
                  <div className="mt-2">
                    <button
                      onClick={() => setShowApprovalModal(true)}
                      className="rounded-md border border-zinc-600 bg-zinc-700 px-3 py-1 text-sm text-zinc-300 transition duration-200 hover:bg-zinc-600"
                    >
                      Manage Functions for Approval (
                      {functionsForApproval.length} selected)
                    </button>
                  </div>
                )}
              </>
            )}

            {!viewOnly && (
              <div className="mt-4 flex justify-end gap-3">
                {mode === 'edit' && (
                  <button
                    className="rounded-md border border-zinc-700 bg-zinc-800 px-4 py-2 text-zinc-300 transition duration-200 hover:bg-zinc-700 focus:outline-none focus:ring-2 focus:ring-zinc-500"
                    onClick={() => onDelete(nodeData.id)}
                  >
                    Delete Node
                  </button>
                )}
                <button
                  className="rounded-md border border-zinc-700 bg-zinc-800 px-4 py-2 text-zinc-300 transition duration-200 hover:bg-zinc-700 focus:outline-none focus:ring-2 focus:ring-zinc-500"
                  onClick={handleSave}
                >
                  {mode === 'add' ? 'Add Node' : 'Save Changes'}
                </button>
              </div>
            )}
          </div>
        </div>
      </Modal>
      {/* --- RENDER SCHEMA BUILDER MODAL --- */}
      <SchemaBuilderModal
        isOpen={isBuilderOpen}
        onClose={() => setIsBuilderOpen(false)}
        onSave={handleSchemaSave}
        initialFields={initialSchemaFields}
        initialModelName={jsonOutputSchema?.title || 'ActionOutput'}
      />
      {showWarningModal && renderWarningModal()}
      {showApprovalModal && renderApprovalModal()} {/* Add this line */}
    </>
  );
};

export default ActionNodeModal;
